import React, { useState, useRef, useEffect } from 'react';
import StatusBadge from './StatusBadge';

const statuses = [
  { value: 'new', label: 'New' },
  { value: 'active', label: 'Active' },
  { value: 'more_evidence_needed', label: 'More Evidence Needed' },
  { value: 'under_consideration', label: 'Under Consideration' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'acknowledged', label: 'Acknowledged' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'denied', label: 'Denied' },
];

export function StatusDropdown({ currentStatus, onUpdate, disabled = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [note, setNote] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleStatusClick = (status) => {
    if (status === currentStatus) {
      setIsOpen(false);
      return;
    }

    setSelectedStatus(status);
    setIsOpen(false);
    setShowNoteModal(true);
  };

  const handleSubmit = async () => {
    if (!userName.trim()) {
      setFormError('Please enter your name');
      return;
    }

    setFormError(null);
    setIsSubmitting(true);
    try {
      await onUpdate(selectedStatus, note.trim() || null, userName.trim());
      setShowNoteModal(false);
      setNote('');
      setSelectedStatus(null);
    } catch (err) {
      setFormError('Failed to update status: ' + (err.message || 'Unknown error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowNoteModal(false);
    setNote('');
    setSelectedStatus(null);
    setFormError(null);
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`flex items-center space-x-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
        >
          <StatusBadge status={currentStatus} size="md" />
          {!disabled && (
            <svg className="w-4 h-4 text-coros-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          )}
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-coros-bg-elevated border border-coros-border rounded-lg shadow-xl z-20 overflow-hidden">
            {statuses.map((status) => (
              <button
                key={status.value}
                onClick={() => handleStatusClick(status.value)}
                className={`w-full px-4 py-2.5 text-left hover:bg-coros-bg-tertiary transition-colors flex items-center justify-between ${
                  status.value === currentStatus ? 'bg-coros-bg-tertiary' : ''
                }`}
              >
                <StatusBadge status={status.value} size="sm" />
                {status.value === currentStatus && (
                  <svg className="w-4 h-4 text-coros-red" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-coros-bg-elevated rounded-xl p-6 w-full max-w-md border border-coros-border">
            <h3 className="text-lg font-semibold text-coros-text-primary mb-4">
              Update Status to <StatusBadge status={selectedStatus} size="sm" />
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-coros-text-muted mb-1">Your Name *</label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="e.g., David Schwartz"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm text-coros-text-muted mb-1">Note (optional)</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note explaining this status change..."
                  rows={3}
                  className="input w-full resize-none"
                />
              </div>
            </div>

            {formError && (
              <p style={{ color: 'var(--color-error)', fontSize: 13, fontWeight: 600, marginTop: 8 }}>
                {formError}
              </p>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={handleCancel}
                disabled={isSubmitting}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default StatusDropdown;
